// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, type ReactNode } from 'react';

import type { Provenance } from '../../../bygning/provenance';
import styles from './Inspector.module.css';
import { ProvenanceTag } from './ProvenanceTag';

export interface FieldRowProps {
  label: string;
  /** The nedrivningsplan reference (Alle felter only). */
  legalRef?: string | null;
  /** A read-only value as text (instead of, or before, a control). */
  text?: ReactNode;
  /** The text is a placeholder ("Ingen prøver"): muted, italic. */
  missing?: boolean;
  /** The control: the value itself, editable. */
  control?: ReactNode;
  tag?: Provenance | null;
  /** Links after the tag ("Brug 17.01.02"). */
  after?: ReactNode;
  /** A line under the value. */
  sub?: ReactNode;
  /** Content below the row (the build-up list, a note field). */
  extra?: ReactNode;
  /** Something the report cannot pass with (warn tone on label and sub). */
  critical?: boolean;
}

/**
 * One inspector row (v8 `crow()`): label (+ reference) on the left; on the
 * right the control as the value, the provenance tag, a sub line and any
 * extra content. The label names the row's group for screen readers.
 */
export function FieldRow({ label, legalRef, text, missing, control, tag, after, sub, extra, critical }: FieldRowProps) {
  const id = useId();
  return (
    <div className={critical ? `${styles.row} ${styles.crit}` : styles.row} role="group" aria-labelledby={id}>
      <div className={styles.label} id={id}>
        {label}
        {legalRef && <small className={styles.ref}>pkt. {legalRef}</small>}
      </div>
      <div className={styles.value}>
        <div className={styles.controls}>
          {text !== undefined && text !== null && (
            <span className={missing ? `${styles.text} ${styles.textMissing}` : styles.text}>{text}</span>
          )}
          {control}
          <ProvenanceTag tag={tag} />
          {after}
        </div>
        {sub && <div className={styles.sub}>{sub}</div>}
        {extra && <div className={styles.extra}>{extra}</div>}
      </div>
    </div>
  );
}
